// Carry Roll Confidence & explanation — PSD §4.2.7. Pure functions.
import { ConfidenceLevel, Fidelity } from './types';
import { mToYd, radToDeg } from './units';
import { DEFAULT_DRIVER_REQUEST } from '../lib/defaultRequest';
// Key fields counted toward "measured" confidence (PSD §4.2.7).
const KEY_FIELD_COUNT = 7;
const DEFAULTS = DEFAULT_DRIVER_REQUEST;
/**
 * Confidence level based on how many key fields are user-measured vs default-assumed.
 * A field is "measured" when it deviates from the default sample values, indicating the
 * user actually entered it.
 */
export function confidence(request) {
    const measured = countMeasured(request);
    if (measured >= KEY_FIELD_COUNT - 1)
        return ConfidenceLevel.Measured;
    if (measured >= 3)
        return ConfidenceLevel.Mixed;
    return ConfidenceLevel.Assumed;
}
function countMeasured(request) {
    let n = 0;
    const l = request.launch;
    const e = request.environment;
    // Count a field as "measured" only when it differs from the sample-driver default.
    if (l.ballSpeed !== DEFAULTS.launch.ballSpeed)
        n++;
    if (l.launchAngle !== DEFAULTS.launch.launchAngle)
        n++;
    if (l.spinRpm !== DEFAULTS.launch.spinRpm)
        n++;
    if (e.altitudeM !== DEFAULTS.environment.altitudeM)
        n++;
    if (e.temperatureK !== DEFAULTS.environment.temperatureK)
        n++;
    if (e.windSpeed !== DEFAULTS.environment.windSpeed)
        n++;
    if (e.firmness !== DEFAULTS.environment.firmness)
        n++;
    return n;
}
/**
 * Build a plain-language explanation of the dominant inputs driving the result.
 * Always non-empty (SC4).
 */
export function buildExplanation(request, result) {
    const parts = [];
    const carryYd = Math.round(mToYd(result.carryM));
    const totalYd = Math.round(mToYd(result.totalM));
    parts.push(`Carry ${carryYd} yd, roll ${Math.round(mToYd(result.rollM))} yd, total ${totalYd} yd.`);
    // Dominant input 1: ball speed
    parts.push(`Driven by ball speed ${Math.round(request.launch.ballSpeed)} m/s at ${radToDeg(request.launch.launchAngle).toFixed(1)}° launch with ${Math.round(request.launch.spinRpm)} rpm spin.`);
    // Dominant input 2: wind
    if (request.environment.windSpeed > 0.5) {
        const headwind = Math.cos((request.environment.windDirDeg * Math.PI) / 180);
        if (headwind > 0.3) {
            parts.push(`Headwind costs distance;`);
        }
        else if (headwind < -0.3) {
            parts.push(`Tailwind adds distance;`);
        }
        else {
            parts.push(`Crosswind shifts the finish;`);
        }
    }
    else {
        parts.push(`Calm wind;`);
    }
    // Dominant input 3: firmness
    const firmLabel = request.environment.firmness === 'firm'
        ? 'firm ground adds roll'
        : request.environment.firmness === 'soft'
            ? 'soft ground limits roll'
            : 'normal ground';
    parts.push(firmLabel + '.');
    // Contextual fidelity caveat — surfaced only when the reduced (Basic) model
    // is in use, woven into the result narrative rather than as a permanent
    // standalone caution banner.
    if (request.fidelity === Fidelity.Basic) {
        parts.push('Basic model — figures are approximate.');
    }
    return parts.join(' ');
}
//# sourceMappingURL=explain.js.map